import React, { useEffect, useState } from 'react';
import Layout from '../../elements/Layout';
import { useParams } from 'react-router-dom';
import { fetchCustomerById, updateCustomer } from '../../utils/api/api';
import toast from 'react-hot-toast';
import Loading from '../../elements/Loading';

// Fixed FormInput component with activeSection as a prop
const FormInput = ({ label, id, name, type = "text", value, onChange, required = false, options = [], activeSection }) => (
    <div className="mb-5">
        <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor={id}>
            {label} {required && <span className="text-primary">*</span>}
        </label>
        {type === "select" ? (
            <div className="relative">
                <select
                    id={id}
                    name={name}
                    value={value || ''}
                    onChange={onChange}
                    className={`appearance-none block w-full px-4 py-3 text-sm rounded-xl border ${activeSection === 'personal' ? 'border-primary/20 focus:border-primary' : 'border-secondary/20 focus:border-secondary'} bg-white/80 backdrop-blur-sm transition-all duration-200 focus:outline-none focus:ring-2 ${activeSection === 'personal' ? 'focus:ring-primary/20' : 'focus:ring-secondary/20'}`}
                    required={required}
                >
                    <option value="">Select {label}</option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                </div>
            </div>
        ) : type === "file" ? (
            <div className="relative">
                <input
                    id={id}
                    type={type}
                    name={name}
                    onChange={onChange}
                    accept="image/*"
                    className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                    required={required}
                />
                <div className={`w-full border-2 border-dashed ${activeSection === 'personal' ? 'border-primary/30' : 'border-secondary/30'} rounded-xl p-4 text-center transition-all duration-200 hover:bg-gray-50`}>
                    <div className="flex flex-col items-center justify-center">
                        <svg className={`w-8 h-8 ${activeSection === 'personal' ? 'text-primary/70' : 'text-secondary/70'} mb-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        <p className="text-sm text-gray-500">
                            {value ? 'Change photo' : 'Upload customer photo'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            JPG, PNG or GIF up to 10MB
                        </p>
                    </div>
                </div>
            </div>
        ) : (
            <input
                id={id}
                type={type}
                name={name}
                value={value || ''}
                onChange={onChange}
                className={`appearance-none block w-full px-4 py-3 text-sm rounded-xl border ${activeSection === 'personal' ? 'border-primary/20 focus:border-primary' : 'border-secondary/20 focus:border-secondary'} bg-white/80 backdrop-blur-sm transition-all duration-200 focus:outline-none focus:ring-2 ${activeSection === 'personal' ? 'focus:ring-primary/20' : 'focus:ring-secondary/20'}`}
                required={required}
            />
        )}
    </div>
);

const EditCustomer = () => {
    const { id } = useParams();
    const customerId = id;
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('personal');
    const [editCustomerData, setEditCustomerData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        id_number: '',
        dob: '',
        gender: '',
        nationality: '',
        city: '',
        state: '',
        country: '',
        next_of_kin1_name: '',
        next_of_kin1_phone: '',
        next_of_kin1_email: '',
        next_of_kin1_idNo: '',
        next_of_kin1_relationship: '',
        next_of_kin2_name: '',
        next_of_kin2_phone: '',
        next_of_kin2_email: '',
        next_of_kin2_idNo: '',
        next_of_kin2_relationship: '',
        customer_type: '',
        photo: '',
        participants: '',
    });

    useEffect(() => {
        const getCustomer = async () => {
            setLoading(true);
            try {
                const data = await fetchCustomerById(customerId);
                setEditCustomerData({ ...data.customer, photo: null });
                setLoading(false);
            } catch (err) {
                toast.error("Failed to load customer data");
                setLoading(false);
            }
        };

        getCustomer();
    }, [customerId]);

    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditCustomerData({
            ...editCustomerData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setEditCustomerData((prevData) => ({
            ...prevData,
            photo: file || null,
        }));
    };

    const handleCustomerFormSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();

        const fieldsToSend = [
            'id',
            'email',
            'first_name',
            'last_name',
            'phone_number',
            'id_number',
            'dob',
            'gender',
            'nationality',
            'city',
            'state',
            'country',
            'next_of_kin1_name',
            'next_of_kin1_phone',
            'next_of_kin1_email',
            'next_of_kin1_idNo',
            'next_of_kin1_relationship',
            'next_of_kin2_name',
            'next_of_kin2_phone',
            'next_of_kin2_email',
            'next_of_kin2_idNo',
            'next_of_kin2_relationship',
            'customer_type',
            'photo',
            'participants',
        ];

        fieldsToSend.forEach((field) => {
            if (editCustomerData[field] !== undefined) {
                formData.append(field, editCustomerData[field]);
            }
        });

        setLoading(true);
        try {
            await updateCustomer(formData);
            toast.success('Customer information updated');
        } catch (error) {
            console.error('Update failed:', error);
            toast.error(error?.response?.data?.message || 'Failed to update customer information');
        } finally {
            setLoading(false);
        }
    };

    // Sections for the form
    const sections = [
        { id: 'personal', label: 'Personal Information' },
        { id: 'location', label: 'Location' },
        { id: 'nextOfKin', label: 'Next of Kin' },
        { id: 'other', label: 'Other Details' }
    ];

    return (
        <Layout text="Update Customer">
            {loading && <Loading />}
            <div className="w-full max-w-5xl mx-auto">
                {/* Progress bar */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        {sections.map((section, index) => (
                            <React.Fragment key={section.id}>
                                <button
                                    onClick={() => setActiveSection(section.id)}
                                    className={`relative flex flex-col items-center group`}
                                >
                                    <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${activeSection === section.id
                                            ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/20'
                                            : sections.indexOf(sections.find(s => s.id === activeSection)) >= index
                                                ? 'bg-green-100 text-green-500 border border-green-200'
                                                : 'bg-gray-100 text-gray-400'
                                        }`}>
                                        {index + 1}
                                    </div>
                                    <span className={`mt-2 text-xs font-medium ${activeSection === section.id
                                            ? 'text-gray-800'
                                            : 'text-gray-500'
                                        }`}>
                                        {section.label}
                                    </span>
                                </button>
                                {index < sections.length - 1 && (
                                    <div className={`flex-1 h-0.5 mx-2 ${sections.indexOf(sections.find(s => s.id === activeSection)) > index
                                            ? 'bg-gradient-to-r from-primary to-secondary'
                                            : 'bg-gray-200'
                                        }`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-300">
                    <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-secondary/5">
                        <h2 className="text-xl font-semibold text-gray-800">
                            {sections.find(s => s.id === activeSection)?.label}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Update the customer's {sections.find(s => s.id === activeSection)?.label.toLowerCase()}
                        </p>
                    </div>

                    <form onSubmit={handleCustomerFormSubmit} className="p-8">
                        {/* Personal Information Section */}
                        {activeSection === 'personal' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormInput
                                    label="First Name"
                                    id="first_name"
                                    name="first_name"
                                    value={editCustomerData.first_name}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="Last Name"
                                    id="last_name"
                                    name="last_name"
                                    value={editCustomerData.last_name}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="Email"
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={editCustomerData.email}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="Phone Number"
                                    id="phone_number"
                                    name="phone_number"
                                    value={editCustomerData.phone_number}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="ID Number"
                                    id="id_number"
                                    name="id_number"
                                    value={editCustomerData.id_number}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="Date of Birth"
                                    id="dob"
                                    name="dob"
                                    type="date"
                                    value={editCustomerData.dob}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="Gender"
                                    id="gender"
                                    name="gender"
                                    type="select"
                                    value={editCustomerData.gender}
                                    onChange={handleEditInputChange}
                                    required
                                    options={[
                                        { value: 'Male', label: 'Male' },
                                        { value: 'Female', label: 'Female' },
                                        { value: 'Other', label: 'Other' }
                                    ]}
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="Customer Type"
                                    id="customer_type"
                                    name="customer_type"
                                    type="select"
                                    value={editCustomerData.customer_type}
                                    onChange={handleEditInputChange}
                                    required
                                    options={[
                                        { value: 'individual', label: 'Individual' },
                                        { value: 'group', label: 'Group' },
                                        { value: 'joint', label: 'Joint' }
                                    ]}
                                    activeSection={activeSection}
                                />
                            </div>
                        )}

                        {/* Location Section */}
                        {activeSection === 'location' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormInput
                                    label="Nationality"
                                    id="nationality"
                                    name="nationality"
                                    value={editCustomerData.nationality}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="City"
                                    id="city"
                                    name="city"
                                    value={editCustomerData.city}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="State/Province"
                                    id="state"
                                    name="state"
                                    value={editCustomerData.state}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                                <FormInput
                                    label="Country"
                                    id="country"
                                    name="country"
                                    value={editCustomerData.country}
                                    onChange={handleEditInputChange}
                                    required
                                    activeSection={activeSection}
                                />
                            </div>
                        )}

                        {/* Next of Kin Section */}
                        {activeSection === 'nextOfKin' && (
                            <div>
                                <div className="mb-8">
                                    <h3 className="text-base font-medium text-gray-700 pb-2 mb-6 border-b border-gray-200">
                                        Primary Next of Kin
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <FormInput
                                            label="Name"
                                            id="next_of_kin1_name"
                                            name="next_of_kin1_name"
                                            value={editCustomerData.next_of_kin1_name}
                                            onChange={handleEditInputChange}
                                            required
                                            activeSection={activeSection}
                                        />
                                        <FormInput
                                            label="Phone"
                                            id="next_of_kin1_phone"
                                            name="next_of_kin1_phone"
                                            value={editCustomerData.next_of_kin1_phone}
                                            onChange={handleEditInputChange}
                                            required
                                            activeSection={activeSection}
                                        />
                                        <FormInput
                                            label="Email"
                                            id="next_of_kin1_email"
                                            name="next_of_kin1_email"
                                            type="email"
                                            value={editCustomerData.next_of_kin1_email}
                                            onChange={handleEditInputChange}
                                            activeSection={activeSection}
                                        />
                                        <FormInput
                                            label="ID Number"
                                            id="next_of_kin1_idNo"
                                            name="next_of_kin1_idNo"
                                            value={editCustomerData.next_of_kin1_idNo}
                                            onChange={handleEditInputChange}
                                            activeSection={activeSection}
                                        />
                                        <FormInput
                                            label="Relationship"
                                            id="next_of_kin1_relationship"
                                            name="next_of_kin1_relationship"
                                            value={editCustomerData.next_of_kin1_relationship}
                                            onChange={handleEditInputChange}
                                            activeSection={activeSection}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-base font-medium text-gray-700 pb-2 mb-6 border-b border-gray-200">
                                        Secondary Next of Kin
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <FormInput
                                            label="Name"
                                            id="next_of_kin2_name"
                                            name="next_of_kin2_name"
                                            value={editCustomerData.next_of_kin2_name}
                                            onChange={handleEditInputChange}
                                            activeSection={activeSection}
                                        />
                                        <FormInput
                                            label="Phone"
                                            id="next_of_kin2_phone"
                                            name="next_of_kin2_phone"
                                            value={editCustomerData.next_of_kin2_phone}
                                            onChange={handleEditInputChange}
                                            activeSection={activeSection}
                                        />
                                        <FormInput
                                            label="Email"
                                            id="next_of_kin2_email"
                                            name="next_of_kin2_email"
                                            type="email"
                                            value={editCustomerData.next_of_kin2_email}
                                            onChange={handleEditInputChange}
                                            activeSection={activeSection}
                                        />
                                        <FormInput
                                            label="ID Number"
                                            id="next_of_kin2_idNo"
                                            name="next_of_kin2_idNo"
                                            value={editCustomerData.next_of_kin2_idNo}
                                            onChange={handleEditInputChange}
                                            activeSection={activeSection}
                                        />
                                        <FormInput
                                            label="Relationship"
                                            id="next_of_kin2_relationship"
                                            name="next_of_kin2_relationship"
                                            value={editCustomerData.next_of_kin2_relationship}
                                            onChange={handleEditInputChange}
                                            activeSection={activeSection}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Other Details Section */}
                        {activeSection === 'other' && (
                            <div className="grid grid-cols-1 gap-6">
                                <FormInput
                                    label="Photo"
                                    id="photo"
                                    name="photo"
                                    type="file"
                                    onChange={handleFileChange}
                                    activeSection={activeSection}
                                />

                                <FormInput
                                    label="Participants (comma separated for group accounts)"
                                    id="participants"
                                    name="participants"
                                    value={editCustomerData.participants}
                                    onChange={handleEditInputChange}
                                    activeSection={activeSection}
                                />
                            </div>
                        )}

                        <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => {
                                    const currentIndex = sections.findIndex(s => s.id === activeSection);
                                    if (currentIndex > 0) {
                                        setActiveSection(sections[currentIndex - 1].id);
                                    }
                                }}
                                className={`px-6 py-3 text-sm font-medium rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all ${activeSection === sections[0].id ? 'opacity-0 pointer-events-none' : ''
                                    }`}
                            >
                                Previous
                            </button>

                            {activeSection === sections[sections.length - 1].id ? (
                                <button
                                    type="submit"
                                    className="px-8 py-3 text-sm font-medium rounded-xl bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
                                >
                                    Update Customer
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const currentIndex = sections.findIndex(s => s.id === activeSection);
                                        if (currentIndex < sections.length - 1) {
                                            setActiveSection(sections[currentIndex + 1].id);
                                        }
                                    }}
                                    className="px-6 py-3 text-sm font-medium rounded-xl bg-gradient-to-r from-primary/90 to-secondary/90 text-white shadow-md shadow-primary/10 hover:shadow-lg hover:shadow-primary/20 transition-all"
                                >
                                    Next
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            {/* Tailwind CSS styles for the gradient buttons and effects */}
            <style jsx>{`
                .bg-grid-white {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='0.1'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
                }
                :root {
                    --color-primary: #4F46E5; /* Indigo 600 */
                    --color-secondary: #0EA5E9; /* Sky 500 */
                }
                .from-primary { from-color: var(--color-primary); }
                .to-primary { to-color: var(--color-primary); }
                .from-secondary { from-color: var(--color-secondary); }
                .to-secondary { to-color: var(--color-secondary); }
                .text-primary { color: var(--color-primary); }
                .text-secondary { color: var(--color-secondary); }
                .border-primary { border-color: var(--color-primary); }
                .border-secondary { border-color: var(--color-secondary); }
                .shadow-primary\/10 { box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.1), 0 2px 4px -1px rgba(79, 70, 229, 0.06); }
                .shadow-primary\/20 { box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.2), 0 2px 4px -1px rgba(79, 70, 229, 0.12); }
                .shadow-primary\/30 { box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.3), 0 2px 4px -1px rgba(79, 70, 229, 0.18); }
            `}</style>
        </Layout>
    );
};

export default EditCustomer;